/**
 * A `MyWorkSource` with no api behind it, for `/__ui/my-work` and the unit tests. Every name
 * here is invented — no production data lands in the repo (CLAUDE.md rule 1).
 */
import { statusColorHex } from '@elizmonday/ui';
import {
  MY_WORK_BUCKETS,
  MY_WORK_BUCKET_LABELS,
  type MyWorkResultView,
  type MyWorkRowView,
  type MyWorkSettingsView,
  type MyWorkSource,
} from './types';

const PEOPLE = [{ id: '5000000000001', name: 'Ada Byron', photoUrl: null }];

function row(id: string, name: string, date: string | null, status: string, done = false): MyWorkRowView {
  return {
    itemId: id,
    name,
    boardId: '1000000000001',
    boardName: 'Sandbox – Column types',
    groupId: 'group1',
    groupTitle: 'Group 1',
    groupColor: statusColorHex('bright_blue'),
    date,
    dateColumnId: 'date',
    status: { text: status, color: statusColorHex(done ? 'done_green' : 'working_orange'), done },
    priority: { text: 'High', color: statusColorHex('dark_purple') },
    people: PEOPLE,
    updateCount: 2,
    unreadUpdateCount: id.endsWith('1') ? 1 : 0,
  };
}

const ROWS: Record<string, MyWorkRowView[]> = {
  past: [
    row('2000000000001', 'Item 01 · Working on it', '2026-06-01T11:30:00.000Z', 'Working on it'),
    row('2000000000002', 'Item 16 · Working on it', '2026-06-16T11:30:00.000Z', 'Working on it'),
    row('2000000000003', 'Item 06 · Working on it', '2026-07-06T00:00:00.000Z', 'Done', true),
  ],
  today: [row('2000000000004', 'Item 11 · Working on it', '2026-09-10T00:00:00.000Z', 'Working on it')],
  this_week: [],
  next_week: [],
  later: [],
  no_date: [row('2000000000005', 'Item 26 · Without a date', null, 'Stuck')],
  done: [],
};

const SETTINGS: MyWorkSettingsView = {
  groupBy: 'date',
  hideDone: false,
  boardIds: null,
  columnRoles: {},
  sort: 'date_asc',
};

export function createFixtureMyWorkSource(overrides: Partial<MyWorkSource> = {}): MyWorkSource {
  let settings = { ...SETTINGS };
  return {
    async list(input): Promise<MyWorkResultView> {
      const term = (input.query ?? '').trim().toLowerCase();
      const sections = MY_WORK_BUCKETS.filter((bucket) => bucket !== 'done' || !settings.hideDone).map(
        (bucket) => {
          const all = (ROWS[bucket] ?? []).filter(
            (candidate) => !(settings.hideDone && candidate.status?.done) &&
              (term.length === 0 || candidate.name.toLowerCase().includes(term)),
          );
          return {
            key: bucket,
            bucket,
            label: MY_WORK_BUCKET_LABELS[bucket],
            count: all.length,
            rows: all,
          };
        },
      );
      return {
        sections,
        boards: [{ id: '1000000000001', name: 'Sandbox – Column types', count: 5 }],
        settings,
      };
    },
    async settings() {
      return settings;
    },
    async saveSettings(patch) {
      settings = { ...settings, ...patch };
      return settings;
    },
    ...overrides,
  };
}
